import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { selectSubscriptionsList, selectSubscriptionsLoading } from '_store/billing/selector';
import { getSubscriptions } from '_store/billing/slice';
import { showModal } from '_store/modals/slice';
import { selectCustomerId } from '_store/user/selector';

import Card from 'components/shared/layouts/Card';
import Text from 'components/shared/layouts/Text';

import SubscriptionsList from './SubscriptionsList';
import SubscriptionsTable from './SubscriptionsTable';

const StyledContainer = styled.div`
  padding-top: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.regular}px;
    padding-bottom: ${theme.spaces.regular}px;
  }
`;

const ROWS_PER_PAGE = 10;

const SubscriptionsView = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const loading = useSelector(selectSubscriptionsLoading);
  const list = useSelector(selectSubscriptionsList);

  const items = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return list?.slice(start, end);
  }, [currentPage, list]);

  const handleUnsubscribe = useCallback(
    ({ orderId, productId, productName }) =>
      () => {
        dispatch(
          showModal({
            key: MODAL_KEYS.cancelSubscriptionConfirm,
            data: { customerId, orderId, productId, productName },
          })
        );
      },
    [customerId]
  );

  useEffect(() => {
    if (customerId) {
      dispatch(getSubscriptions({ customerId }));
    }
  }, [customerId]);

  return (
    <StyledContainer>
      <Card>
        <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
          Treatments
        </Text>
        <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
          Treatments
        </Text>

        {isMobile && (
          <SubscriptionsList
            loading={loading}
            items={items}
            pageCount={list?.length / ROWS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onUnsubscribe={handleUnsubscribe}
          />
        )}
        {!isMobile && (
          <SubscriptionsTable
            skeletonRowsNum={ROWS_PER_PAGE}
            loading={loading}
            items={items}
            pageCount={list?.length / ROWS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onUnsubscribe={handleUnsubscribe}
          />
        )}
      </Card>
    </StyledContainer>
  );
};

export default SubscriptionsView;
